
import { Button, Col, Form, Row, Space, Tag, Typography } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteChildrenProps } from "react-router";
import { VALIDATION_MESSAGE_CONFIG } from "../../../constants/constants";
import { ROUTE_PATHS } from "../../../constants/url-config";
import { MessageType } from "../../../models/common/response-message";
import { AllOnboardingModel, InviteUserModel, RoleModel } from "../../../models/onboarding/onboarding";
import { ClientService } from "../../../services/client.service";
import { DATE_UTIL } from "../../../utils/dateUtils";
import i18n from "../../../utils/i18n";
import { showMessage } from "../../atoms/notification/notification";
import AddUsers from "./add-users/add-users";
import CorporateInformation from "./corporate-information/corporate-information";
import "./onboarding.scss";
import ReivewData from "./review-data";
const { Text } = Typography;

interface Step {
    id: number;
    title: string;
    description?: string;
    component: (props: OnboardingStepProps) => JSX.Element;
}

export interface OnboardingStepProps {
    users?: InviteUserModel[];
    addUser?: (values: InviteUserModel) => Promise<void>;
    deleteUser?: (username: string) => void;
    updateUser?: (values: InviteUserModel) => Promise<void>;
    model?: AllOnboardingModel;
    useRoles?: RoleModel[];
}

export const userTableColumns = [
    {
        title: i18n.t("Onboarding.lbName"),
        dataIndex: 'name',
        key: 'name',
        render: (text: string, record: InviteUserModel) =>
            <Text className="blue-text table-cell-text" style={{ width: 100 }}>
                {`${record.first_name ?? ""} ${record.last_name ?? ""}`}
            </Text>
    },
    {
        title: i18n.t("Onboarding.lbEmail"),
        dataIndex: 'email',
        key: 'email',
        render: (text: string) =>
            <Text className="table-cell-text" style={{ width: 150 }}>
                {text}
            </Text>
    },
    {
        title: i18n.t("Onboarding.lbRole"),
        dataIndex: 'roles',
        key: 'roles',
        render: (text: string, record: InviteUserModel) => (
            <React.Fragment>
                <Space direction="vertical" size="small" className="role-tag">
                    {record.roles && record.roles.map((r, index) =>
                        <React.Fragment key={index}>
                            {
                                index <= 1 &&
                                <Tag title={r.display_name} color="default">
                                    {r.display_name}
                                </Tag>
                            }
                        </React.Fragment>
                    )}
                </Space>
                {
                    (record.roles && record.roles.length > 2) &&
                    <span className="text-more">
                        ...
                    </span>
                }
            </React.Fragment>

        )

    }
]

interface Props extends RouteChildrenProps { }

export default function OnBoarding(props: Props) {
    const { t } = useTranslation();
    const [currentStep, setCurrentStep] = useState(0);
    const [model, setModel] = useState<AllOnboardingModel>({});
    const [form] = Form.useForm();
    const [users, setUsers] = useState<InviteUserModel[]>([]);
    const [useRoles, setUserRoles] = useState<RoleModel[]>([]);

    const [steps] = useState<Step[]>(
        [
            {
                id: 1,
                title: t("Onboarding.ttCorporateInformation"),
                description: t("Onboarding.desCorporateInformation"),
                component: CorporateInformation
            },
            {
                id: 2,
                title: t("Onboarding.ttAddUsers"),
                description: t("Onboarding.desAddUsers"),
                component: AddUsers
            },
            {
                id: 3,
                title: t("Onboarding.ttReviewYourData"),
                component: ReivewData
            }
        ]
    )

    const createUser = async (user: InviteUserModel) => {
        await ClientService.inviteUser(user);
        getUsers();
    }

    const updateUser = async (user: InviteUserModel) => {
        await ClientService.updateUser(user);
        getUsers();
    }

    const deleteUser = (username: string) => {
        ClientService.deleteUser(username).then(res => {
            getUsers();
        })
    }

    const formSubmit = (values: AllOnboardingModel) => {
        setModel(values);
        setCurrentStep(step => step + 1);
    }

    const next = () => {
        setCurrentStep(step => {
            if (step === 0) {
                form.submit();
                return step;
            }

            return step + 1;
        });
    }

    const goback = () => {
        setCurrentStep(step => step - 1);
    }

    const submit = () => {
        ClientService.generalOnboarding(model).then(res => {
            showMessage({ message: "Submit Successfully", type: MessageType.success });
            props.history.push(ROUTE_PATHS.Home);
        })
    }

    const getUsers = () => {
        ClientService.getUsers().then(res => {
            setUsers(res.data);
        });
    }

    const inintSetting = () => {
        ClientService.getClientInfo().then(res => {
            const data: AllOnboardingModel = res.data;
            data.next_quarter_end_date = DATE_UTIL.convertServerToDatePicker(data.next_quarter_end_date?.toString());
            data.next_year_end_date = DATE_UTIL.convertServerToDatePicker(data.next_year_end_date?.toString());
            form.setFieldsValue(data);
        });
    }

    useEffect(() => {
        getUsers();
        ClientService.getResourcesAsync().then(res => {
            setUserRoles(res.data.user_roles);
        });
        inintSetting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className='onboarding-wrapper'>
            <Row gutter={20}>
                <Col xs={0} sm={5}>

                </Col>
                <Col xs={0} sm={14}>
                    <div className="main-form">

                        <Form
                            layout='vertical'
                            form={form}
                            validateMessages={VALIDATION_MESSAGE_CONFIG}
                            onFinish={(values) => formSubmit(values)}
                            scrollToFirstError>

                            {
                                steps && steps.map((item, index) =>
                                    <React.Fragment key={item.id}>
                                        {
                                            currentStep === index &&
                                            <div>

                                                <div className="form-header">

                                                    <div className="title">
                                                        {item.title}
                                                    </div>
                                                    <p>
                                                        {item.description}
                                                    </p>
                                                </div>

                                                <div>
                                                    <item.component
                                                        addUser={createUser}
                                                        users={users}
                                                        deleteUser={deleteUser}
                                                        updateUser={updateUser}
                                                        model={model}
                                                        useRoles={useRoles} />
                                                </div>
                                            </div>
                                        }

                                    </React.Fragment>
                                )
                            }

                        </Form>

                        <div className="slide-dot-wrapper">
                            {
                                steps && steps.map((item, index) =>
                                    <div key={index} className={`slide-dot ${currentStep === index ? "active" : ""}`} >

                                    </div>)
                            }
                        </div>

                        <Row gutter={12} className="form-action">
                            <Col span={12}>
                                {
                                    currentStep > 0 &&
                                    <Button type="default" onClick={goback} block shape="round">
                                        {t("Onboarding.btnGoback")}
                                    </Button>
                                }

                            </Col>
                            <Col span={12}>
                                {

                                    currentStep === steps.length - 1 ?
                                        <Button type="primary" onClick={submit} block shape="round">
                                            {t("Onboarding.btnSubmit")}
                                        </Button> : <Button type="primary" onClick={next} block shape="round">
                                            {t("Onboarding.btnNext")}
                                        </Button>
                                }
                            </Col>
                        </Row>
                    </div>

                </Col>
                <Col xs={0} sm={5}>
                </Col>
            </Row>
        </div >
    )

}